/**
 * Checklists — `me/checklists` and `team/checklists` (prototype VIEWS.checklists, docs/prototype.html L1015–L1046,
 * with ckSetupBar L4326–L4333 and the hub-mode head() rule L755).
 *
 *  - GET /checklists → the four stats, "This week" and "By client" (scope: managers all clients, a member their own);
 *  - GET /checklist-templates, POST /checklist-templates, POST /checklists/apply-template → "Templates";
 *  - GET /clients/{id}/checklists + GET /clients/{id}/routines → the client's checklist panel, where
 *    POST /clients/{id}/checklists starts one from a template;
 *  - POST|DELETE /checklist-runs/{id}/done and /routine-instances/{id}/done → tick with proof / reopen.
 *
 * Proof is a note: no proof-upload route exists yet (docs/architecture/step7-contract-decisions.md, "Open for the
 * operator"), so there is no file input that would go nowhere.
 */
import { useMemo, useState } from 'react';
import { ROUTINE_EVERY_LABEL, Segment, isManagerRole, type ChecklistItemState, type ChecklistTemplate } from '@amplispot/contracts';
import {
  STATE_CHIP,
  periodMonth,
  useApplyTemplate,
  useChecklistsOverview,
  useClientChecklists,
  useCreateClientChecklist,
  useCreateTemplate,
  useRoutines,
  useTemplates,
  useTick,
  type TickKind,
} from '../../lib/checklists';
import { firstName } from '../../lib/format';
import { useSession } from '../../lib/session';
import { Avatar, Drawer, Empty, ErrorState, Field, Loading, Modal, errorText, useFail } from '../../ui/kit';
import { Stat } from '../timesheets/TimesheetReport';
import { AddRoutineModal, BulkRoutinesModal, ClientPicker, PrioMark, RoutineDrawer, useScopeClients } from './Routine';

const tickSvg = (
  <svg viewBox="0 0 12 12" aria-hidden="true">
    <path d="M2 6.5l2.6 2.6L10 3.5" />
  </svg>
);

type Tab = 'week' | 'clients' | 'templates';
/** Viewing as someone (ADR-006 view mode) refuses every write: ticks stay visible as state but cannot be pressed. */
const RO_TITLE = 'Read-only while you view as someone';
type Tick = { kind: TickKind; id: string; text: string };
type ClientOpen = { id: string; name: string };

export function Checklists() {
  const { me, readOnly } = useSession();
  const q = useChecklistsOverview();
  const [tab, setTab] = useState<Tab>('week');
  const [tick, setTick] = useState<Tick | null>(null);
  const [open, setOpen] = useState<ClientOpen | null>(null);
  const [routineOf, setRoutineOf] = useState<ClientOpen | null>(null);
  const mgr = isManagerRole(me.user.role);

  if (q.isPending) return <Loading />;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  const o = q.data;
  const month = periodMonth(o.period);

  return (
    <>
      {mgr ? (
        <div className="ph" style={{ margin: '0 0 12px', justifyContent: 'flex-end' }}>
          <div className="r" style={{ marginLeft: 0 }}>
            <span className="chip brand">{month}</span>
          </div>
        </div>
      ) : null}
      {o.can_set_up && !readOnly ? <SetupBar onSeeRoutine={setRoutineOf} /> : null}
      <div className="stats">
        <Stat k="Items this week and month" v={o.stats.items} />
        <Stat k="Done" v={o.stats.done} s="with proof" cls="green" />
        <Stat k="Missed" v={o.stats.missed} s="past their due day" cls="red" />
        <Stat k="Due today" v={o.stats.due_today} cls="amber" />
      </div>
      <div className="tabs" role="tablist">
        {(
          [
            ['week', 'This week'],
            ['clients', 'By client'],
            ['templates', 'Templates'],
          ] as const
        ).map(([k, l]) => (
          <button key={k} className={tab === k ? 'on' : ''} onClick={() => setTab(k)} role="tab" aria-selected={tab === k}>
            {l}
          </button>
        ))}
      </div>

      {tab === 'week' ? (
        <div className="panel">
          <div className="hd">
            <h3>Due in the next 7 days, or already missed</h3>
            <div className="r">{o.this_week.length} items</div>
          </div>
          {o.this_week.length ? (
            o.this_week.map((x, i) => (
              <div className="li" key={x.id ?? `${x.client.id}:${x.text}:${i}`}>
                <button
                  className="chk"
                  aria-label="Mark done"
                  disabled={!x.id || readOnly}
                  title={readOnly ? RO_TITLE : x.id ? 'Mark done' : 'Its run is made on the due day; it can be ticked once it shows in the plan'}
                  style={x.id && !readOnly ? undefined : { opacity: 0.5, cursor: 'not-allowed' }}
                  onClick={() => x.id && !readOnly && setTick({ kind: x.kind, id: x.id, text: x.text })}
                />
                <div className="b">
                  <div className="t">{x.text}</div>
                  <div className="m">
                    <span>
                      <PrioMark priority={x.client.priority} />
                      {x.client.name}
                    </span>
                    <span>{firstName(x.client.spoc.name)}</span>
                    <span className={`chip ${x.kind === 'routine_instance' ? 'brand' : ''}`}>{x.cadence}</span>
                  </div>
                </div>
                <span className={`chip ${STATE_CHIP[x.state]}`}>{x.label}</span>
              </div>
            ))
          ) : (
            <Empty title="All caught up">Nothing is due in the next 7 days and nothing is missed.</Empty>
          )}
        </div>
      ) : tab === 'clients' ? (
        <div className="panel" style={{ overflowX: 'auto' }}>
          {o.by_client.length ? (
            <table>
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Owner</th>
                  <th>Template</th>
                  <th>Progress</th>
                  <th>Missed</th>
                </tr>
              </thead>
              <tbody>
                {o.by_client.map((r) => (
                  <tr
                    key={r.client.id}
                    className="row"
                    tabIndex={0}
                    onClick={() => setOpen({ id: r.client.id, name: r.client.name })}
                    onKeyDown={(e) => e.key === 'Enter' && setOpen({ id: r.client.id, name: r.client.name })}
                  >
                    <td>
                      <b>{r.client.name}</b>
                      <PrioMark priority={r.client.priority} />
                    </td>
                    <td>
                      <Avatar user={r.client.spoc} />
                    </td>
                    <td>{r.template ?? <span className="sub">No checklist</span>}</td>
                    <td style={{ minWidth: 150 }}>
                      <div className="split">
                        <div className="meter grow">
                          <i style={{ width: `${r.progress_pct}%` }} />
                        </div>
                        <span className="num" style={{ fontSize: 12 }}>
                          {r.progress_pct}%
                        </span>
                      </div>
                    </td>
                    <td>{r.missed_count ? <span className="chip cold">{r.missed_count}</span> : <span className="chip hot">0</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <Empty title="No clients yet">Clients you look after show here with their checklist progress.</Empty>
          )}
        </div>
      ) : (
        <TemplatesTab manager={mgr && !readOnly} />
      )}

      {tick ? <TickModal tick={tick} onClose={() => setTick(null)} /> : null}
      {open ? (
        <ClientChecklistDrawer
          client={open}
          month={month}
          onClose={() => setOpen(null)}
          onTick={setTick}
        />
      ) : null}
      {routineOf ? (
        <RoutineDrawer
          clientId={routineOf.id}
          clientName={routineOf.name}
          onClose={() => setRoutineOf(null)}
          onOpenChecklist={() => {
            setOpen(routineOf);
            setRoutineOf(null);
          }}
        />
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------- Set up a checklist

function SetupBar({ onSeeRoutine }: { onSeeRoutine: (c: ClientOpen) => void }) {
  const scope = useScopeClients();
  const [picked, setPicked] = useState('');
  const [adding, setAdding] = useState(false);
  const [bulk, setBulk] = useState(false);
  const cs = scope.data;
  if (scope.isError)
    return (
      <div className="panel" style={{ marginBottom: 16 }}>
        <div className="bd" style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
          <div className="grow" style={{ minWidth: 240 }}>
            <b style={{ fontSize: 15 }}>Set up a checklist</b>
            <div className="sub" style={{ fontSize: 13, marginTop: 2, color: 'var(--cold)' }}>
              Your clients did not load, so a checklist cannot be set up right now. {errorText(scope.error)}
            </div>
          </div>
          <button className="btn" onClick={() => void scope.refetch()} disabled={scope.isFetching}>
            {scope.isFetching ? 'Trying…' : 'Try again'}
          </button>
        </div>
      </div>
    );
  if (!scope.isPending && !cs.length) return null;
  const client = cs.find((c) => c.id === picked) ?? cs[0];

  return (
    <div className="panel" style={{ marginBottom: 16 }}>
      <div className="bd" style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
        <div className="grow" style={{ minWidth: 240 }}>
          <b style={{ fontSize: 15 }}>Set up a checklist</b>
          <div className="sub" style={{ fontSize: 13, marginTop: 2 }}>
            Pick a client, then add what has to happen every day, week or month.{' '}
            {scope.any ? 'You can set it up for any client.' : 'You can set it up for your own clients.'}
          </div>
        </div>
        <select
          className="in"
          style={{ width: 'auto', minWidth: 200, borderRadius: 99 }}
          value={client?.id ?? ''}
          onChange={(e) => setPicked(e.target.value)}
          disabled={scope.isPending}
          aria-label="Client"
        >
          {scope.isPending ? <option value="">Loading clients…</option> : null}
          {cs.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <button className="btn pri" onClick={() => setAdding(true)} disabled={!client}>
          Add a recurring task
        </button>
        <button className="btn" onClick={() => client && onSeeRoutine({ id: client.id, name: client.name })} disabled={!client}>
          See their routine
        </button>
        <button className="btn" onClick={() => setBulk(true)} disabled={!client}>
          For many clients
        </button>
      </div>
      {adding && client ? <AddRoutineModal client={client} onClose={() => setAdding(false)} /> : null}
      {bulk ? <BulkRoutinesModal onClose={() => setBulk(false)} /> : null}
    </div>
  );
}

// ---------------------------------------------------------------- Templates

/** "Send previous month MIS, 3" → text + day. A line with no day falls on the 15th (prototype addTemplate). */
function parseLines(txt: string): { items: { text: string; due_day_of_month: number }[]; bad: number[] } {
  const items: { text: string; due_day_of_month: number }[] = [];
  const bad: number[] = [];
  txt
    .split('\n')
    .map((l) => l.trim())
    .forEach((l, i) => {
      if (!l) return;
      const at = l.lastIndexOf(',');
      const tail = at >= 0 ? l.slice(at + 1).trim() : '';
      if (at >= 0 && /^\d+$/.test(tail)) {
        const d = Number(tail);
        if (d < 1 || d > 31) bad.push(i + 1);
        items.push({ text: l.slice(0, at).trim(), due_day_of_month: d });
      } else items.push({ text: (at >= 0 && !tail ? l.slice(0, at) : l).trim(), due_day_of_month: 15 });
    });
  return { items, bad };
}

function TemplatesTab({ manager }: { manager: boolean }) {
  const { can } = useSession();
  const q = useTemplates();
  const [give, setGive] = useState<ChecklistTemplate | null>(null);
  const writeAny = can('checklist.write.any');
  const list = (q.data ?? []).filter((t) => t.active);

  return (
    <div className={writeAny ? 'cols c2' : undefined}>
      <div className="panel" style={{ alignSelf: 'start' }}>
        <div className="hd">
          <h3>Templates</h3>
        </div>
        {q.isPending ? (
          <Loading />
        ) : q.isError ? (
          <ErrorState error={q.error} onRetry={() => void q.refetch()} />
        ) : list.length ? (
          list.map((t) => {
            const items = t.items.filter((i) => i.active);
            return (
              <div className="li" key={t.id}>
                <div className="b">
                  <div className="t">
                    {t.name} {t.segment ? <span className="chip">{t.segment}</span> : null}
                  </div>
                  <div className="m">{items.map((i) => `${i.text} (day ${i.due_day_of_month})`).join(' · ')}</div>
                </div>
                <span className="chip">{items.length} items</span>
                {manager ? (
                  <button className="btn sm" onClick={() => setGive(t)}>
                    Give to clients
                  </button>
                ) : null}
              </div>
            );
          })
        ) : (
          <Empty title="No templates yet">{writeAny ? 'Add one on the right: one activity per line with its due day.' : 'A manager adds the standard monthly lists.'}</Empty>
        )}
      </div>
      {writeAny ? <AddTemplate /> : null}
      {give ? <ApplyTemplateModal template={give} onClose={() => setGive(null)} /> : null}
    </div>
  );
}

function AddTemplate() {
  const create = useCreateTemplate();
  const { errs, setErrs, fail, toast } = useFail(['name', 'segment', 'items']);
  const [txt, setTxt] = useState('');
  const [name, setName] = useState('');
  const [segment, setSegment] = useState('');

  const save = () => {
    const { items, bad } = parseLines(txt);
    const local: Record<string, string> = {};
    if (!items.length) local.items = 'Add at least one line';
    else if (bad.length) local.items = `Line ${bad.join(', ')}: the due day must be from 1 to 31`;
    else if (items.some((i) => i.text.length < 3)) local.items = 'Each line needs a few words about what has to be done';
    if (name.trim().length < 2) local.name = 'Give it a name of at least 2 characters';
    setErrs(local);
    if (Object.keys(local).length) return;
    create.mutate(
      { name: name.trim(), segment: (segment || null) as Segment | null, items },
      {
        onSuccess: (t) => {
          toast(`${t.items.length} activities saved`);
          setTxt('');
          setName('');
          setSegment('');
        },
        onError: (e) => {
          fail(e);
          // `items.3.text` and the like go under the textarea.
          const f = (e as { fieldErrors?: Record<string, string> }).fieldErrors;
          const itemErr = f && Object.entries(f).find(([k]) => /^items/.test(k));
          if (itemErr) setErrs((p) => ({ ...p, items: itemErr[1] }));
        },
      },
    );
  };

  return (
    <div className="panel" style={{ alignSelf: 'start' }}>
      <div className="hd">
        <h3>Add a template</h3>
      </div>
      <div className="bd">
        <Field label="Paste one activity per line, then a comma and the due day of the month" error={errs.items}>
          <textarea className="in" rows={7} value={txt} onChange={(e) => setTxt(e.target.value)} placeholder={'Send previous month MIS, 3\nClient health call, 10'} aria-invalid={!!errs.items || undefined} />
        </Field>
        <Field label="Template name" error={errs.name}>
          <input className="in" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Insurance monthly ops" maxLength={80} aria-invalid={!!errs.name || undefined} />
        </Field>
        <Field label="For segment" error={errs.segment} hint="One active template per segment. Leave it as None for an extra list.">
          <select className="in" value={segment} onChange={(e) => setSegment(e.target.value)}>
            <option value="">None</option>
            {Segment.options.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </Field>
        <button className="btn pri" onClick={save} disabled={create.isPending}>
          {create.isPending ? 'Saving…' : 'Save template'}
        </button>
      </div>
    </div>
  );
}

function ApplyTemplateModal({ template, onClose }: { template: ChecklistTemplate; onClose: () => void }) {
  const scope = useScopeClients();
  const apply = useApplyTemplate();
  const { errs, setErrs, fail, toast } = useFail(['client_ids']);
  const [sel, setSel] = useState<Set<string>>(new Set());
  const names = useMemo(() => new Map(scope.data.map((c) => [c.id, c.name])), [scope.data]);

  const submit = () => {
    if (!sel.size) {
      setErrs({ client_ids: 'Choose at least one client' });
      return;
    }
    apply.mutate(
      { template_id: template.id, client_ids: [...sel] },
      {
        onSuccess: (r) => {
          const had = r.skipped.filter((s) => s.reason === 'already_has_it');
          const other = r.skipped.length - had.length;
          toast(
            `Given to ${r.applied.length} client${r.applied.length === 1 ? '' : 's'}${
              had.length ? `. ${had.length === 1 ? `${names.get(had[0]!.client_id) ?? '1 client'} already has it` : `${had.length} already have it`}` : ''
            }${other ? `. ${other} skipped` : ''}.`,
          );
          onClose();
        },
        onError: fail,
      },
    );
  };

  return (
    <Modal
      wide
      title={`Give “${template.name}” to clients`}
      sub="Each client you choose gets this list as their monthly checklist. A client that already has it is left as it is."
      onClose={onClose}
      footer={
        <>
          <span className="sub">{errs.client_ids && !sel.size ? <span style={{ color: 'var(--cold)' }}>{errs.client_ids}</span> : `${sel.size} client${sel.size === 1 ? '' : 's'} chosen`}</span>
          <div className="grow" />
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn pri" onClick={submit} disabled={apply.isPending}>
            {apply.isPending ? 'Giving…' : 'Give to clients'}
          </button>
        </>
      }
    >
      {scope.isPending ? <Loading label="Loading clients…" /> : scope.isError ? <ErrorState error={scope.error} onRetry={() => void scope.refetch()} /> : <ClientPicker clients={scope.data} value={sel} onChange={setSel} />}
    </Modal>
  );
}

// ---------------------------------------------------------------- one client's checklist

type Row = {
  key: string;
  text: string;
  kind: TickKind;
  id: string | null;
  done: boolean;
  state: ChecklistItemState;
  label: string;
  cadence: string;
  routine: boolean;
  proof: string | null;
};

function ClientChecklistDrawer({ client, month, onClose, onTick }: { client: ClientOpen; month: string; onClose: () => void; onTick: (t: Tick) => void }) {
  const { readOnly } = useSession();
  const lists = useClientChecklists(client.id);
  const routines = useRoutines(client.id);
  const templates = useTemplates();
  const tick = useTick();
  const make = useCreateClientChecklist();
  const { fail, toast } = useFail([]);
  const [tpl, setTpl] = useState('');

  const pending = lists.isPending || routines.isPending;
  const error = lists.error ?? routines.error;
  const active = (lists.data ?? []).filter((l) => l.active);
  const rows: Row[] = [
    ...active.flatMap((l) =>
      l.items
        .filter((i) => i.active)
        .map((i) => ({
          key: `c:${i.id}`,
          text: i.text,
          kind: 'checklist_run' as const,
          id: i.this_period.run_id,
          done: i.this_period.done,
          state: i.this_period.state,
          label: i.this_period.label,
          cadence: 'Monthly',
          routine: false,
          proof: i.this_period.proof ? (i.this_period.proof.note ?? 'file attached') : null,
        })),
    ),
    ...(routines.data?.routines ?? [])
      .filter((r) => r.current)
      .map((r) => ({
        key: `r:${r.id}`,
        text: r.what,
        kind: 'routine_instance' as const,
        id: r.current!.id,
        done: r.current!.done,
        state: r.current!.state,
        label: r.current!.label,
        cadence: ROUTINE_EVERY_LABEL[r.every],
        routine: true,
        proof: r.current!.proof ? (r.current!.proof.note ?? 'file attached') : null,
      })),
  ];
  const canEdit = (routines.data?.can_edit ?? false) && !readOnly;
  const segment = routines.data?.client.segment;
  const tpls = (templates.data ?? []).filter((t) => t.active && t.items.some((i) => i.active));
  const preferred = tpls.find((t) => t.segment === segment) ?? tpls[0];
  const chosen = tpls.find((t) => t.id === tpl) ?? preferred;

  const untick = (r: Row) =>
    r.id && tick.mutate({ kind: r.kind, id: r.id, proof: null }, { onSuccess: () => toast('Reopened. Proof cleared.'), onError: fail });

  return (
    <Drawer title={`${client.name} · ${month}`} onClose={onClose}>
      {pending ? (
        <Loading />
      ) : error ? (
        <ErrorState
          error={error}
          onRetry={() => {
            void lists.refetch();
            void routines.refetch();
          }}
        />
      ) : (
        <>
          <div className="note" style={{ marginBottom: 14 }}>
            Mark an item done and add proof.
          </div>
          {rows.map((r) => (
            <div className="li" style={{ padding: '12px 0' }} key={r.key}>
              <button
                className={`chk${r.done ? ' on' : ''}`}
                aria-label={r.done ? 'Reopen' : 'Mark done'}
                aria-pressed={r.done}
                disabled={!r.id || readOnly || tick.isPending}
                title={readOnly ? RO_TITLE : r.id ? (r.done ? 'Reopen' : 'Mark done') : 'Its run is made on the due day; it can be ticked then'}
                style={r.id && !readOnly ? undefined : { opacity: r.done ? 0.8 : 0.5, cursor: 'not-allowed' }}
                onClick={() => (readOnly ? undefined : r.done ? untick(r) : r.id && onTick({ kind: r.kind, id: r.id, text: r.text }))}
              >
                {r.done ? tickSvg : null}
              </button>
              <div className="b">
                <div className="t">{r.text}</div>
                <div className="m">
                  <span className={`chip ${STATE_CHIP[r.state]}`}>{r.label}</span>
                  <span className={`chip ${r.routine ? 'brand' : ''}`}>{r.cadence}</span>
                  {r.proof ? <span>Proof: {r.proof}</span> : null}
                </div>
              </div>
            </div>
          ))}
          {!active.length ? (
            canEdit ? (
              <div className="panel" style={{ marginTop: rows.length ? 14 : 0 }}>
                <div className="bd">
                  <b>No monthly checklist yet</b>
                  <div className="sub" style={{ margin: '2px 0 10px' }}>
                    Start from a template. Its items are copied, and a task appears in the SPOC’s plan on each due day.
                  </div>
                  {templates.isPending ? (
                    <Loading label="Loading templates…" />
                  ) : templates.isError ? (
                    <ErrorState error={templates.error} onRetry={() => void templates.refetch()} />
                  ) : tpls.length ? (
                    <>
                      <Field label="Start from a template">
                        <select className="in" value={chosen?.id ?? ''} onChange={(e) => setTpl(e.target.value)}>
                          {tpls.map((t) => (
                            <option key={t.id} value={t.id}>
                              {t.name}
                              {t.segment ? ` · ${t.segment}` : ''} · {t.items.filter((i) => i.active).length} items
                            </option>
                          ))}
                        </select>
                      </Field>
                      <button
                        className="btn pri"
                        disabled={!chosen || make.isPending}
                        onClick={() =>
                          chosen &&
                          make.mutate(
                            { clientId: client.id, body: { template_id: chosen.id, name: chosen.name } },
                            { onSuccess: () => toast(`Checklist set up from ${chosen.name}`), onError: fail },
                          )
                        }
                      >
                        {make.isPending ? 'Setting up…' : 'Set up checklist'}
                      </button>
                    </>
                  ) : (
                    <div className="sub">No templates yet. A manager adds one under Templates.</div>
                  )}
                </div>
              </div>
            ) : rows.length ? null : (
              <Empty title="No checklist yet">This client has no monthly checklist or recurring task yet.</Empty>
            )
          ) : null}
        </>
      )}
    </Drawer>
  );
}

// ---------------------------------------------------------------- Proof for "<item>"

function TickModal({ tick, onClose }: { tick: Tick; onClose: () => void }) {
  const t = useTick();
  const { errs, setErrs, fail, toast } = useFail(['note']);
  const [note, setNote] = useState('');
  const submit = () => {
    if (note.trim().length < 3) {
      setErrs({ note: 'Write a few words about what was done' });
      return;
    }
    t.mutate(
      { kind: tick.kind, id: tick.id, proof: { note: note.trim() } },
      {
        onSuccess: () => {
          toast('Done, with proof');
          onClose();
        },
        onError: fail,
      },
    );
  };
  const err = errs.note;
  return (
    <Modal
      title={`Proof for “${tick.text}”`}
      sub="Add a short note about what was done. It is kept with the item."
      onClose={onClose}
      footer={
        <>
          <div className="grow" />
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn pri" onClick={submit} disabled={t.isPending}>
            {t.isPending ? 'Saving…' : 'Mark done'}
          </button>
        </>
      }
    >
      <Field label="Note" error={err}>
        <textarea
          className="in"
          value={note}
          onChange={(e) => {
            setNote(e.target.value);
            if (err) setErrs({});
          }}
          placeholder="e.g. Sent to Farah on email, thread linked"
          maxLength={1000}
          aria-invalid={!!err || undefined}
          autoFocus
        />
      </Field>
    </Modal>
  );
}
